import { Avatar, Badge, Group, Stack, Text } from '@mantine/core';
import type { MRT_Cell, MRT_ColumnDef } from 'mantine-react-table';

import { useTranslation } from 'react-i18next';

import type { Role } from '../../../shared/entities/Access';
import type { WorkspaceUser } from '../../../shared/entities/User';
import { formatDateInParisTZ } from '../../../shared/utils/dates';

export enum Column {
  USER = 'user',
  ROLE = 'role',
  DATE_ADDED = 'date-added',
  STATUS = 'status',
}

type ColumnType = MRT_ColumnDef<WorkspaceUser>;

export function useColumnsDefinition() {
  const { t } = useTranslation(['workspaceSettings', 'entities']);

  const user: ColumnType = {
    id: Column.USER,
    header: t('workspaceUserTable.name'),
    accessorFn: user => user,
    Cell: ({ cell }: { cell: MRT_Cell<WorkspaceUser> }) => {
      const user = cell.getValue<WorkspaceUser>();

      return (
        <Group noWrap spacing="02" maw={164} align="flex-start">
          <Avatar size={32} radius="md" color={user.avatarColor}>
            <Text c="gray.9">{user.firstName[0] + user.lastName[0]}</Text>
          </Avatar>

          <Stack spacing={0} align="flex-start" w={124}>
            <Text
              variant="sm"
              fw={500}
              c="gray.9"
              lineClamp={1}
            >{`${user.firstName} ${user.lastName}`}</Text>
            <Text variant="xs" c="gray.6" fw={400} truncate>
              {user.email}
            </Text>
          </Stack>
        </Group>
      );
    },
  };

  const role: ColumnType = {
    id: Column.ROLE,
    enableColumnFilter: false,
    filterFn: 'roleFilter',
    header: t('workspaceUserTable.role'),
    accessorFn: user => user.role,
    Cell: ({ cell }: { cell: MRT_Cell<WorkspaceUser> }) => {
      const role = cell.getValue<Role>();
      if (!role) {
        return null;
      }
      return (
        <Badge
          bg="gray.0"
          c="gray.9"
          fw={400}
          sx={theme => ({
            textTransform: 'none',
            border: `1px solid ${theme.colors.gray[1]}`,
          })}
        >
          {t(`entities:role.${role}`)}
        </Badge>
      );
    },
  };

  const dateAdded: ColumnType = {
    id: Column.DATE_ADDED,
    header: t('workspaceUserTable.dateAdded'),
    accessorFn: user => formatDateInParisTZ(user.addedToWorkspaceAt),
    size: 50,
  };

  const status: ColumnType = {
    id: Column.STATUS,
    header: t('status.label'),
    accessorKey: 'disabledAt',
    Cell: ({ cell }: { cell: MRT_Cell<WorkspaceUser> }) => {
      const disabledAt = cell.getValue<WorkspaceUser['disabledAt']>();

      return disabledAt ? (
        <DeactivatedStatusBadge label={t('status.deactivated')} />
      ) : (
        <ActivatedStatusBadge label={t('status.activated')} />
      );
    },
    size: 50,
  };

  return {
    user,
    role,
    dateAdded,
    status,
  };
}

function DeactivatedStatusBadge({ label }: { label: string }) {
  return (
    <Badge
      bg="red.1"
      c="gray.9"
      styles={theme => ({
        root: {
          textTransform: 'none',
          borderColor: theme.colors.red[2],
        },
      })}
    >
      {label}
    </Badge>
  );
}

function ActivatedStatusBadge({ label }: { label: string }) {
  return (
    <Badge
      bg="teal.1"
      c="gray.9"
      styles={theme => ({
        root: {
          textTransform: 'none',
          borderColor: theme.colors.teal[3],
        },
      })}
    >
      {label}
    </Badge>
  );
}
